import { useTranslation } from "next-i18next";
import Link from "next/link";
import React from "react";
import CallToActionSmall from "../components/v2/call-to-action-sm";
import Card from "../components/v2/cards/card";
import CardWithIcon from "../components/v2/cards/card-with-icon";
import {
    CompetitiveSalary,
    CustomerMw,
    FlexiblePTO, FreeLunches, Healthcare,
    MwGoal, MwSmallIcon,
    StockOptions,
    TrainingCareerGrowth,
    WorkHardMw
} from "../components/v2/icons";
import CareerSlider from "../components/v2/mw-carousel/career-page";
import OurPeopleSay from "../components/v2/mw-carousel/our-people";
import { careerJobs } from "../lib/career-jobs";
import "../styles/scss/career.module.scss";

const iconMap: { [key: string]: React.ReactNode } = {
    CompetitiveSalary: <CompetitiveSalary />,
    StockOptions: <StockOptions />,
    Healthcare: <Healthcare />,
    FlexiblePTO: <FlexiblePTO />,
    TrainingCareerGrowth: <TrainingCareerGrowth />,
    FreeLunches: <FreeLunches />
};

const Career = () => {
    const { t } = useTranslation('common');
    const careerData = t('career', { returnObjects: true, defaultValue: {} }) as any;

    const heroData = careerData?.hero || {};
    const benefitsData = careerData?.benefits || {};
    const whyMiddlewareData = careerData?.whyMiddleware || {};
    const perksBenefitsData = careerData?.perksBenefits || {};
    const openPositionsData = careerData?.openPositions || {};

    const careerCards = Array.isArray(whyMiddlewareData?.cards) ? whyMiddlewareData.cards : [];
    const perksCards = Array.isArray(perksBenefitsData?.cards) ? perksBenefitsData.cards : [];
    const benefitsItems = Array.isArray(benefitsData?.items) ? benefitsData.items : [];
    const openJobs = careerJobs;

    return (
        <>
            <div className="middle-wrapper site-page careerV2">
                <div className={"hero-section"}>
                    <div className={"container"}>
                        <div className={"d-flex"}>
                            <div className={"left-block"}>
                                <h1 dangerouslySetInnerHTML={{ __html: `${String(heroData?.titlePrefix ?? '')}<span class="text-gradient"> ${String(heroData?.titleHighlight ?? '')}</span>` }}></h1>
                                <Link href={String(heroData?.cta?.link ?? '')} className={"sign-up ahref-btn primary"}>{String(heroData?.cta?.text ?? '')}</Link>
                            </div>
                            <div className={"right-block"}>
                                <img src={"/images/career/hero-bg.svg"} alt={"Middleware Career"} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={"slider-image"}>
                    <CareerSlider />
                </div>

                <div className={"container"}>
                    <div className={"career-benefit gradient-border-bottom gradient-border-top "}>
                        <ul>
                            <li><p><MwGoal /> {String(benefitsItems[0] ?? '')}</p></li>
                            <li><p><CustomerMw /> {String(benefitsItems[1] ?? '')}</p></li>
                            <li><p><WorkHardMw /> {String(benefitsItems[2] ?? '')}</p></li>
                        </ul>
                    </div>
                </div>

                <section className={'mw-card career'}>
                    <div className={'container'}>
                        <div className={'d-flex card-items'}>
                            <div className={'card-item'}>
                                <div className={'section-tag testimonial pd-right'}><MwSmallIcon />{String(whyMiddlewareData?.sectionTag ?? '')}</div>
                                <h2>
                                    {String(whyMiddlewareData?.titlePrefix ?? '')}
                                    <span className={"text-gradient"}>{String(whyMiddlewareData?.titleHighlight ?? '')}</span>
                                </h2>
                            </div>
                            {
                                careerCards.map((item: any, index: number) => {
                                    return <Card
                                        key={index}
                                        heading={String(item?.heading ?? '')}
                                        description={String(item?.description ?? '')}
                                        isHeading={item?.isHeading === true}
                                    />
                                })
                            }
                        </div>
                    </div>
                </section>


                <section className={'mw-card-with-icon bg-light-blue career-page'}>
                    <div className={'container'}>
                        <div className={'section-tag testimonial pd-right'}><MwSmallIcon /> {String(perksBenefitsData?.sectionTag ?? '')}</div>
                        <h2>
                            {String(perksBenefitsData?.titlePrefix ?? '')}
                            <span>{String(perksBenefitsData?.titleHighlight ?? '')}</span>
                        </h2>
                        <div className={'card-items'}>
                            <div className={'d-flex card-items'}>
                                {perksCards.slice(0, 3).map((card: any, index: number) => (
                                    <CardWithIcon
                                        key={index}
                                        icon={iconMap[String(card?.icon ?? '')] || null}
                                        title={String(card?.title ?? '')}
                                        description={String(card?.description ?? '')}
                                    />
                                ))}
                            </div>
                            <div className={'d-flex card-items'}>
                                {perksCards.slice(3, 6).map((card: any, index: number) => (
                                    <CardWithIcon
                                        key={index + 3}
                                        icon={iconMap[String(card?.icon ?? '')] || null}
                                        title={String(card?.title ?? '')}
                                        description={String(card?.description ?? '')}
                                    />
                                ))}
                            </div>

                        </div>
                    </div>
                </section>

                <section className={"mw-employee-testimonial divide-two"}>
                    <OurPeopleSay />
                </section>

                <section className={"mw-job-opening"} id={"open_position"}>
                    <div className={"container"}>
                        <div className='section-tag testimonial'><MwSmallIcon /> Open Positions</div>
                        <h2>We Are <span className={"text-gradient"}> Hiring! </span></h2>
                        <div className="job-listing">
                            {[0, 1, 2].map((groupIndex) => (
                                <div className="career-flex-card" key={groupIndex}>
                                    {openJobs.slice(groupIndex * 2, groupIndex * 2 + 2).map((job) => (
                                        <div className="d-flex" key={job.slug}>
                                            <div className="left-block">
                                                <h4>{job.title.replace(/<[^>]*>/g, " ")}</h4>
                                                <div className="job-details">
                                                    <p>Location: {job.location}</p>
                                                    <p>Type: {job.type}</p>
                                                </div>
                                            </div>
                                            <div className="right-block">
                                                <Link href={`/careers/${job.slug}/`} className={"read-more"}>
                                                    Apply Now
                                                </Link>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <CallToActionSmall />
            </div>
        </>
    )
}

export default Career